import React, { useEffect, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { addMember } from '../../features/members/membersSlice';
import { toast } from 'react-toastify';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { RootState } from '../../app/store';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';

type InviteMemberModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type Role = {
  id: string;
  tenantId: string;
  name: string;
  systemRole: boolean;
  permissions: string[];
  createdAt: string;
  updatedAt: string;
  version: number;
  archived: boolean;
  deletedAt: string | null;
};

type RolesResponse = {
  roles: Role[];
};

const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch();
  const members = useSelector((state: RootState) => state.members.list);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Fetch roles from API
  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        return;
      }

      const response = await fetch(`${BASE_URL}/v1/team/roles`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch roles: ${response.status}`);
      }

      const data: RolesResponse = await response.json();
      setRoles(data.roles);
    } catch (error: any) {
      // console.error('Failed to fetch roles:', error);
      toast.error('Failed to load roles');
    } finally {
      setLoadingRoles(false);
    }
  };

  // Fetch roles when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchRoles();
    }
  }, [isOpen]);

  // Defines validation schema for invite form with email uniqueness check
  const InviteSchema = Yup.object().shape({
    email: Yup.string()
      .email('Invalid email format')
      .required('Email is required')
      .test('unique-email', 'This member is already invited', function (value) {
        if (!value) return true;
        return !members.some(
          (member) => member.email.toLowerCase() === value.toLowerCase()
        );
      }),
    role: Yup.string().required('Role is required'),
    message: Yup.string(),
  });

  // Handles form submission - calls API and then updates Redux
  const handleSubmit = async (
    values: { email: string; role: string; message: string },
    { resetForm, setSubmitting }: { resetForm: () => void; setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    try {
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        return;
      }

      // Generate Idempotency-Key
      const idempotencyKey = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;


      // Show loading toast
      const loadingToast = toast.loading('Sending invitation...');

      const response = await fetch(`${BASE_URL}/v1/team/invites`, {
        method: 'POST',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Idempotency-Key': idempotencyKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: values.email,
          roleId: values.role,
          message: values.message || '',
        }),
      });

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      if (!response.ok) {
        let errorMessage = `Failed to send invite: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // Ignore JSON parsing error
        }
        throw new Error(errorMessage);
      }

      // Get the role name for display
      const selectedRole = roles.find(role => role.id === values.role);
      const roleName = selectedRole ? selectedRole.name : values.role;

      // Add new member to Redux state immediately
      const newMember = {
        name: '-',
        email: values.email,
        role: roleName,
        lastLogin: '-',
        addedDate: new Date().toLocaleDateString('en-GB'),
        status: 'Pending' as const,
      };

      dispatch(addMember(newMember));
      toast.success('Invite sent successfully!');
      resetForm();
      onClose();
    } catch (error: any) {
      // console.error('Invite error:', error);
      toast.error(error.message || 'Failed to send invite');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 flex items-center justify-center bg-black/50 z-50'>
      <div className='bg-white w-[480px] rounded-lg shadow-lg relative'>
        {/* Header */}
        <div className='flex justify-between items-center px-6 py-4 border-b'>
          <h2 className='text-lg font-semibold'>Invite Member</h2>
          <button
            onClick={onClose}
            className='text-gray-500 hover:text-gray-700'
          >
            <FiXComponent size={20} />
          </button>
        </div>

        {/* Formik Form */}
        <Formik
          initialValues={{ email: '', role: '', message: '' }}
          validationSchema={InviteSchema}
          onSubmit={handleSubmit}
        >
          {({ isValid, dirty, isSubmitting }) => (
            <Form>
              {/* Body */}
              <div className='px-6 py-4 space-y-4'>
                {/* Email */}
                <div>
                  <label className='block text-sm font-medium text-gray-700'>
                    Enter Email<span className='text-red-500'>*</span>
                  </label>
                  <Field
                    name='email'
                    type='email'
                    placeholder='Enter member’s email'
                    className='mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#00A4BD] outline-none'
                  />
                  <ErrorMessage
                    name='email'
                    component='div'
                    className='text-red-500 text-xs mt-1'
                  />
                </div>

                {/* Role */}
                <div>
                  <label className='block text-sm font-medium text-gray-700'>
                    Assign Role<span className='text-red-500'>*</span>
                  </label>
                  <Field
                    as='select'
                    name='role'
                    className='mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#00A4BD] outline-none'
                    disabled={loadingRoles}
                  >
                    <option value=''>
                      {loadingRoles ? 'Loading roles...' : 'Select'}
                    </option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='role'
                    component='div'
                    className='text-red-500 text-xs mt-1'
                  />
                </div>

                {/* Message */}
                <div>
                  <label className='block text-sm font-medium text-gray-700'>
                    Message
                  </label>
                  <Field
                    as='textarea'
                    name='message'
                    placeholder='Write a message to the member'
                    className='mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#00A4BD] outline-none'
                  />
                </div>
              </div>

              {/* Footer */}
              <div className='flex justify-end gap-3 px-6 py-4 border-t'>
                <button
                  type='button'
                  onClick={onClose}
                  className='px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-100'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={!(isValid && dirty) || loadingRoles || isSubmitting}
                  className={`px-4 py-2 rounded-md text-white ${isValid && dirty && !loadingRoles && !isSubmitting
                    ? 'bg-[#FF6B57] hover:bg-[#e65a48]'
                    : 'bg-gray-300 cursor-not-allowed'
                    }`}
                >
                  {isSubmitting ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default InviteMemberModal;